/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CommunicationPage } from "../models/CommunicationPage";
import type { MessageCreateOutbound } from "../models/MessageCreateOutbound";
import type { MessageCreateTutorStudents } from "../models/MessageCreateTutorStudents";
import type { MessageResponse } from "../models/MessageResponse";
import type { ReadStateResult } from "../models/ReadStateResult";
import type { ReadStateUpdate } from "../models/ReadStateUpdate";
import type { UnreadSummary } from "../models/UnreadSummary";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class MessagesService {
  /**
   * Get User Communication
   * Get the communication history for a user
   * @returns any Successful Response
   * @throws ApiError
   */
  public static getUserCommunication({
    studentId,
    tutorId,
    parentId,
    startDate,
    endDate,
  }: {
    studentId?: number | null;
    tutorId?: number | null;
    parentId?: number | null;
    startDate?: string | null;
    endDate?: string | null;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/messages/user/communication",
      query: {
        student_id: studentId,
        tutor_id: tutorId,
        parent_id: parentId,
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Organization Communication
   * The newest page of the organization's communication in the date range
   * (get_organization_communication_page has the rest), plus this admin's
   * unread conversations whatever their date, newest first. Inbound
   * messages are read or unread as this admin sees them.
   * @returns any Successful Response
   * @throws ApiError
   */
  public static getOrganizationCommunication({
    orgId,
    startDate,
    endDate,
  }: {
    orgId: number;
    startDate?: string | null;
    endDate?: string | null;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/messages/organization/communication",
      query: {
        org_id: orgId,
        start_date: startDate,
        end_date: endDate,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Organization Communication Page
   * A page of the organization's communication in the date range: up to
   * PAGE_SIZE messages, newest first, older than (before, before_id) when
   * given (the last page's next_before and next_before_id). The first page
   * also has this admin's unread conversations whatever their date. Inbound
   * messages are read or unread as this admin sees them.
   * @returns CommunicationPage Successful Response
   * @throws ApiError
   */
  public static getOrganizationCommunicationPage({
    orgId,
    startDate,
    endDate,
    before,
    beforeId,
  }: {
    orgId: number;
    startDate?: string | null;
    endDate?: string | null;
    before?: string | null;
    beforeId?: number | null;
  }): CancelablePromise<CommunicationPage> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/messages/organization/communication/page",
      query: {
        org_id: orgId,
        start_date: startDate,
        end_date: endDate,
        before: before,
        before_id: beforeId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Organization Unread Summary
   * What is unread for this admin: conversations by channel and filled-out
   * forms. The bell shows the total; the dashboard's Communication icon the
   * conversations. Polled every minute.
   * @returns UnreadSummary Successful Response
   * @throws ApiError
   */
  public static getOrganizationUnreadSummary({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<UnreadSummary> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/messages/organization/unread_summary",
      query: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Set Read State
   * Mark inbound messages and the bell's events read or unread for this
   * admin only (opening, the envelope or dot, Mark all read, Undo). Returns
   * what is unread after, and what it marked.
   * @returns ReadStateResult Successful Response
   * @throws ApiError
   */
  public static setReadState({
    requestBody,
  }: {
    requestBody: ReadStateUpdate;
  }): CancelablePromise<ReadStateResult> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/read_state",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Mark Message As Read
   * Mark a message as read for this admin only.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static markMessageAsRead({
    messageId,
  }: {
    messageId: number;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/mark_message_as_read",
      query: {
        message_id: messageId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Recipients
   * Create a new message to specified recipients
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToRecipients({
    requestBody,
  }: {
    requestBody: MessageCreateOutbound;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Org
   * Create a new message to an organization's members
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToOrg({
    requestBody,
  }: {
    requestBody: MessageCreateOutbound;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/organizations",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Org Tutors
   * Create a new message to an organization's tutors
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToOrgTutors({
    requestBody,
  }: {
    requestBody: MessageCreateOutbound;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/organizations/tutors",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Org Students
   * Create a new message to an organization's students
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToOrgStudents({
    requestBody,
  }: {
    requestBody: MessageCreateOutbound;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/organizations/students",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Org Parents
   * Create a new message to an organization's parents
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToOrgParents({
    requestBody,
  }: {
    requestBody: MessageCreateOutbound;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/organizations/parents",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message To Tutor Students
   * Create a new message to a tutor's students
   * and return the resulting message from the database.
   * @returns MessageResponse Successful Response
   * @throws ApiError
   */
  public static createMessageToTutorStudents({
    requestBody,
  }: {
    requestBody: MessageCreateTutorStudents;
  }): CancelablePromise<MessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/messages/by_tutor/{tutor_id}/students",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
